import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { ErrorNote, SubmitButton } from '@/components/ui';

export function LoginPage() {
  const { user, login, loading } = useAuth();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState(false);

  if (loading) return null;
  if (user) {
    const to = user.mustChangePassword ? '/change-password' : ((location.state as { from?: string } | null)?.from ?? '/');
    return <Navigate to={to} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await login(username.trim(), password);
    } catch (err) {
      setError(err);
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-center gap-2">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-brand-600 text-lg font-bold text-white">T</span>
          <span className="text-xl font-semibold tracking-tight">TickTock</span>
        </div>

        <form onSubmit={onSubmit} className="card space-y-4 p-6">
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Sign in</h1>
            <p className="mt-0.5 text-sm text-slate-500">Use your username or email address.</p>
          </div>

          <ErrorNote error={error} />

          <div>
            <label className="label" htmlFor="username">
              Username or email
            </label>
            <input
              id="username"
              className="input"
              autoComplete="username"
              autoFocus
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>

          <div>
            <label className="label" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              type="password"
              className="input"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <SubmitButton pending={pending} className="btn-primary w-full">
            Sign in
          </SubmitButton>

          <p className="text-center text-sm">
            <Link to="/forgot-password" className="text-brand-700 hover:underline">
              Forgot your password?
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
